import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, BASE_URL } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { getSocket } from '../utils/socket';
import ScoreChart from '../components/admin/ScoreChart';
import type { GroupScore, ScoreHistoryEntry, ScoreHistoryGroup, SeriesMetadata } from '../types';

const CARD_H = 90;
const CARD_GAP = 12;
const MEDALS = ['🥇', '🥈', '🥉'];

function KahootLeaderboard({ scores }: { scores: GroupScore[] }) {
  const prevScores = useRef<Record<number, number>>({});
  const [pulsing, setPulsing] = useState<Set<number>>(new Set());

  useEffect(() => {
    const changed = new Set<number>();
    scores.forEach((s) => {
      if (prevScores.current[s.id] !== undefined && prevScores.current[s.id] !== s.totalPoints) {
        changed.add(s.id);
      }
      prevScores.current[s.id] = s.totalPoints;
    });
    if (changed.size > 0) {
      setPulsing(changed);
      const t = setTimeout(() => setPulsing(new Set()), 900);
      return () => clearTimeout(t);
    }
  }, [scores]);

  if (scores.length === 0) {
    return <p className="kahoot-empty">Aucune équipe enregistrée.</p>;
  }

  const totalHeight = scores.length * CARD_H + (scores.length - 1) * CARD_GAP;

  return (
    <div className="kahoot-board" style={{ height: totalHeight, position: 'relative' }}>
      {scores.map((s) => {
        const y = (s.rank - 1) * (CARD_H + CARD_GAP);
        // 22 = hex for ~13% opacity
        const bgColor = s.color + '22';
        return (
          <div
            key={s.id}
            className={`kahoot-row${pulsing.has(s.id) ? ' kahoot-pulse' : ''}`}
            style={{
              transform: `translateY(${y}px)`,
              background: bgColor,
              borderLeftColor: s.color,
            }}
          >
            <div className="kahoot-rank">{MEDALS[s.rank - 1] ?? `#${s.rank}`}</div>
            <div className="kahoot-name">{s.groupName}</div>
            <div className="kahoot-score">
              {s.totalPoints}
              <span className="kahoot-pts-label"> pts</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminScores() {
  const [scores, setScores] = useState<GroupScore[]>([]);
  const [history, setHistory] = useState<ScoreHistoryEntry[]>([]);
  const [historyGroups, setHistoryGroups] = useState<ScoreHistoryGroup[]>([]);
  const [seriesMetadata, setSeriesMetadata] = useState<SeriesMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<'charts' | 'kahoot'>('kahoot');
  const { token } = useAuth();

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [{ groups }, hist] = await Promise.all([
        api.get<{ groups: GroupScore[] }>('/api/scores'),
        api.get<{ history: ScoreHistoryEntry[]; groups: ScoreHistoryGroup[]; seriesMetadata: SeriesMetadata[] }>('/api/scores/history'),
      ]);
      setScores(groups);
      setHistory(hist.history);
      setHistoryGroups(hist.groups);
      setSeriesMetadata(hist.seriesMetadata ?? []);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const socket = getSocket();

    const handleScoresUpdate = ({ scores: s }: { scores: GroupScore[] }) => {
      setScores(s);
    };
    const handleReveal = () => load(true);

    socket.on('scores:update', handleScoresUpdate);
    socket.on('question:reveal', handleReveal);
    socket.on('serie:intru_reveal', handleReveal);
    socket.on('game:started', handleReveal);
    socket.on('game:ended', handleReveal);

    return () => {
      socket.off('scores:update', handleScoresUpdate);
      socket.off('question:reveal', handleReveal);
      socket.off('serie:intru_reveal', handleReveal);
      socket.off('game:started', handleReveal);
      socket.off('game:ended', handleReveal);
    };
  }, [load]);

  const downloadCSV = (endpoint: string, filename: string) => {
    const url = `${BASE_URL}${endpoint}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objUrl);
      });
  };

  const exportCSV = () => downloadCSV('/api/scores/csv', 'scores.csv');
  const exportDetailedCSV = () => downloadCSV('/api/scores/detailed-csv', 'resultats-detailles.csv');

  // Per-series history slices with relative scores (start from 0 for each serie)
  const seriesCharts = useMemo(() => {
    return seriesMetadata.map((s) => {
      const entries = history.filter((h) => h.serieId === s.id);
      if (entries.length === 0) return null;

      const firstIdx = history.indexOf(entries[0]);
      const baseScores = firstIdx > 0 ? history[firstIdx - 1].scores : {};

      const relativeHistory = entries.map((h) => ({
        ...h,
        scores: Object.fromEntries(
          Object.entries(h.scores).map(([gId, score]) => [gId, score - (baseScores[Number(gId)] ?? 0)])
        ),
      }));

      return { serie: s, entries: relativeHistory };
    }).filter(Boolean) as Array<{ serie: SeriesMetadata; entries: ScoreHistoryEntry[] }>;
  }, [history, seriesMetadata]);

  return (
    <div className="admin-page">
      <div className="page-header">
        <h2>Scores</h2>
        <div className="btn-row">
          <button className="btn btn-secondary" onClick={() => load()}>Actualiser</button>
          <button className="btn btn-primary" onClick={exportCSV}>Exporter CSV</button>
          <button className="btn btn-secondary" onClick={exportDetailedCSV}>Exporter CSV détaillé</button>
        </div>
      </div>

      {/* View toggle */}
      <div className="scores-view-tabs">
        <button
          className={`scores-view-tab ${activeView === 'kahoot' ? 'active' : ''}`}
          onClick={() => setActiveView('kahoot')}
        >
          🏆 Classement
        </button>
        <button
          className={`scores-view-tab ${activeView === 'charts' ? 'active' : ''}`}
          onClick={() => setActiveView('charts')}
        >
          📊 Graphiques
        </button>
      </div>

      {loading ? (
        <div className="page-loading">Chargement...</div>
      ) : activeView === 'kahoot' ? (
        <KahootLeaderboard scores={scores} />
      ) : (
        <>
          {/* Global evolution chart — only played questions */}
          {history.length > 0 ? (
            <div className="card">
              <h3 className="card-title">Évolution globale — {history.filter(h => h.questionId).length} questions jouées</h3>
              <ScoreChart history={history} groups={historyGroups} />
            </div>
          ) : (
            <p className="text-muted" style={{ textAlign: 'center', padding: '2rem' }}>
              Aucune question jouée pour le moment.
            </p>
          )}

          {/* Per-series charts — one per played serie */}
          {seriesCharts.length > 0 && (
            <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
              <h3>Scores par série</h3>
              {seriesCharts.map(({ serie, entries }) => (
                <div key={serie.id} className="card">
                  <h4 style={{ marginBottom: 12, borderBottom: '1px solid var(--border)', paddingBottom: 8 }}>
                    {serie.name}
                    <span className="text-muted" style={{ fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
                      — {entries.filter(e => e.questionId).length} question{entries.filter(e => e.questionId).length !== 1 ? 's' : ''} jouée{entries.filter(e => e.questionId).length !== 1 ? 's' : ''}
                    </span>
                  </h4>
                  <ScoreChart history={entries} groups={historyGroups} />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
